import type { TOC } from '@ember/component/template-only'
import { t } from 'ember-intl'
import { SideNavAccordionItem, SideNavAccordionList } from 'teacher-ui-client/components/side-nav/accordion'
import { service } from '@ember/service'
import type FeatureControlService from 'teacher-ui-client/services/feature-control'
import type SideNavService from 'teacher-ui-client/services/side-nav'
import Component from '@glimmer/component'
import type LogoutService from 'teacher-ui-client/services/logout'
import { join } from 'teacher-ui-client/utils/uri'
import config from 'teacher-ui-client/config/environment'
import PhosphorIcon from 'teacher-ui-client/components/phosphor-icon'
import { on } from '@ember/modifier'
import { SubscriptionType } from 'teacher-ui-client/subscription-type/subscription-types'
import type Features from 'teacher-ui-client/services/features'
import pencilUnfilled from '@images/icons/pencil-unfilled.svg'

interface Signature {
  Element: HTMLElement
}

export const ManagementAccordion: TOC<Signature> = <template>
  <SideNavAccordionList
    data-test-menu-item-management
    @iconUrl={{pencilUnfilled}}
    @iconAlt="Sidenav pencil"
    @label={{t "sideNav.management"}}
  >
    <ManageStudentsItem />
    <ManageClassesItem />
    <SideNavAccordionItem @route="manage.progress">
      {{t "manage.title.progress"}}
    </SideNavAccordionItem>
    <ManageTeachersItem />
    <ManageRolloverItem />
    <ManageSubscriptionsItem />
    <ManageCertificatesItem />

  </SideNavAccordionList>
</template>

class ManageCertificatesItem extends Component {
  @service sideNav!: SideNavService

  @service features!: Features

  get isVisible() {
    const isWriting = this.sideNav.currentSubscriptionType === SubscriptionType.Writing
    const writingFeatureEnabled = this.features.isEnabled('tui_writing_certificates')

    return !isWriting || writingFeatureEnabled
  }

  <template>
    {{#if this.isVisible}}
      <SideNavAccordionItem @route="manage.certificates">
        {{t "manage.title.certificates"}}
      </SideNavAccordionItem>
    {{/if}}
  </template>
}

class ManageStudentsItem extends Component {
  @service featureControl!: FeatureControlService
  @service sideNav!: SideNavService
  @service logout!: LogoutService

  get oneRosterUrl() {
    if (!this.featureControl.canAccess('one_roster')) return

    const baseUrl = join(config.oneRosterUrl, 'login')
    const studentsUrl = new URL(baseUrl)
    studentsUrl.searchParams.set('return_to', '/manage-students')
    return studentsUrl.toString()
  }

  logoutAndRedirectTo = () => {
    void this.logout.logoutAndRedirectToReadingeggs('/parent_ui/redirect/home')
  }

  <template>
    {{#if this.sideNav.isHomeschool}}
      <SideNavAccordionItem @asButton={{true}} {{on "click" this.logoutAndRedirectTo}}>
        {{t "manage.title.sideNavStudents"}}
      </SideNavAccordionItem>

    {{else if this.oneRosterUrl}}
      <SideNavAccordionItem
        data-test-manage-students-link
        href={{this.oneRosterUrl}}
        target="_blank"
        rel="noopener noreferrer"
      >
        {{t "manage.title.sideNavStudents"}}
        <PhosphorIcon @icon="phosphor-arrow-square-out-bold" />
      </SideNavAccordionItem>

    {{else}}
      <SideNavAccordionItem @route="manage.students" @currentWhen="manage.students manage.import.students">
        {{t "manage.title.sideNavStudents"}}
      </SideNavAccordionItem>

    {{/if}}
  </template>
}

class ManageClassesItem extends Component {
  @service featureControl!: FeatureControlService
  @service sideNav!: SideNavService

  get isVisible() {
    return !this.sideNav.isHomeschool
  }

  get oneRosterUrl() {
    if (!this.featureControl.canAccess('one_roster')) return

    const baseUrl = join(config.oneRosterUrl, 'login')
    const classesUrl = new URL(baseUrl)
    classesUrl.searchParams.set('return_to', '/manage-classes')
    return classesUrl.toString()
  }

  <template>
    {{#if this.isVisible}}
      {{#if this.oneRosterUrl}}
        <SideNavAccordionItem
          data-test-manage-classes-link
          href={{this.oneRosterUrl}}
          target="_blank"
          rel="noopener noreferrer"
        >
          {{t "manage.title.classes"}}
          <PhosphorIcon @icon="phosphor-arrow-square-out-bold" />
        </SideNavAccordionItem>

      {{else}}
        <SideNavAccordionItem @route="manage.classes" @currentWhen="manage.classes">
          {{t "manage.title.classes"}}
        </SideNavAccordionItem>
      {{/if}}
    {{/if}}
  </template>
}

class ManageTeachersItem extends Component {
  @service featureControl!: FeatureControlService

  @service sideNav!: SideNavService

  get isVisible() {
    return this.sideNav.isSubco && !this.sideNav.isHomeschool
  }

  get oneRosterUrl() {
    if (!this.featureControl.canAccess('one_roster')) return

    const baseUrl = join(config.oneRosterUrl, 'login')
    const teachersUrl = new URL(baseUrl)
    teachersUrl.searchParams.set('return_to', '/manage-teachers')
    return teachersUrl.toString()
  }

  <template>
    {{#if this.isVisible}}
      {{#if this.oneRosterUrl}}
        <SideNavAccordionItem
          data-test-manage-teachers-link
          href={{this.oneRosterUrl}}
          target="_blank"
          rel="noopener noreferrer"
        >
          {{t "manage.title.teachers"}}
          <PhosphorIcon @icon="phosphor-arrow-square-out-bold" />
        </SideNavAccordionItem>

      {{else}}
        <SideNavAccordionItem @route="manage.teachers" @currentWhen="manage.teachers manage.import.teachers">
          {{t "manage.title.teachers"}}
        </SideNavAccordionItem>
      {{/if}}
    {{/if}}
  </template>
}

class ManageRolloverItem extends Component {
  @service featureControl!: FeatureControlService
  @service sideNav!: SideNavService

  get isVisible() {
    return this.sideNav.isSubco && !this.sideNav.isSchoolExternallyManaged && !this.sideNav.isHomeschool
  }

  get oneRosterUrl() {
    if (!this.featureControl.canAccess('one_roster')) return

    const baseUrl = join(config.oneRosterUrl, 'login')
    const teachersUrl = new URL(baseUrl)
    teachersUrl.searchParams.set('return_to', '/manage-classes/rollover/setup')
    return teachersUrl.toString()
  }

  <template>
    {{#if this.isVisible}}
      {{#if this.oneRosterUrl}}
        <SideNavAccordionItem
          data-test-manage-rollover-link
          href={{this.oneRosterUrl}}
          target="_blank"
          rel="noopener noreferrer"
        >
          {{t "manage.title.rollover"}}
          <PhosphorIcon @icon="phosphor-arrow-square-out-bold" />
        </SideNavAccordionItem>

      {{else}}
        <SideNavAccordionItem
          data-test-manage-rollover-link
          @route="manage.rollover.wizard"
          @currentWhen="manage.rollover"
        >
          {{t "manage.title.rollover"}}
        </SideNavAccordionItem>
      {{/if}}
    {{/if}}
  </template>
}

class ManageSubscriptionsItem extends Component {
  @service featureControl!: FeatureControlService

  @service sideNav!: SideNavService

  get isVisible() {
    return this.sideNav.isSubco && !this.sideNav.isHomeschool
  }

  get oneRosterUrl() {
    if (!this.featureControl.canAccess('one_roster')) return

    const baseUrl = join(config.oneRosterUrl, 'login')
    const subscriptionsUrl = new URL(baseUrl)
    subscriptionsUrl.searchParams.set('return_to', '/manage-subscriptions')
    return subscriptionsUrl.toString()
  }

  <template>
    {{#if this.isVisible}}
      {{#if this.oneRosterUrl}}
        <SideNavAccordionItem
          data-test-manage-subscription-link
          href={{this.oneRosterUrl}}
          target="_blank"
          rel="noopener noreferrer"
        >
          {{t "manage.title.subscription"}}
          <PhosphorIcon @icon="phosphor-arrow-square-out-bold" />
        </SideNavAccordionItem>

      {{else}}
        <SideNavAccordionItem @route="manage.subscription" @currentWhen="manage.subscription">
          {{t "manage.title.subscription"}}
        </SideNavAccordionItem>
      {{/if}}
    {{/if}}
  </template>
}
